import { useMemo, useState } from "react";
import type { RegistrationCollections } from "@tu/conference-connector-contract";
import { collectionChartDays } from "./registration-collections";
import { useLocale } from "./localization";
import { RiyalAmount } from "./RiyalAmount";

export function RegistrationCollectionsChart({
  data,
  stale,
}: {
  readonly data: RegistrationCollections | null | undefined;
  readonly stale: boolean;
}) {
  const { t, number, date, currency } = useLocale();
  const [range, setRange] = useState<"all" | "30" | "90">("all");
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const days = useMemo(
    () => (data ? collectionChartDays(data, range) : []),
    [data, range],
  );
  const selected = days.find((day) => day.date === selectedDate) ?? days.at(-1);
  const totals = days.reduce(
    (sum, day) => ({
      count: sum.count + day.invoiceCount,
      amount: sum.amount + day.amountMinor,
    }),
    { count: 0, amount: 0 },
  );
  const maximum = Math.max(1, ...days.map((day) => day.invoiceCount));
  const width = Math.max(660, days.length * 26 + 80);
  const plotWidth = width - 80;
  const labelEvery = Math.max(
    1,
    Math.ceil(days.length / Math.max(6, Math.floor(width / 100))),
  );
  const dayLabel = (day: string) =>
    date(`${day}T12:00:00Z`, {
      year: "numeric",
      month: "short",
      day: "numeric",
      timeZone: "Asia/Riyadh",
    });
  const pointLabel = (day: (typeof days)[number]) =>
    `${dayLabel(day.date)} — ${t("collections.count")}: ${number(day.invoiceCount)} — ${currency(day.amountMinor / 100, "SAR")}`;
  return (
    <section
      className="detail-panel registration-collections"
      aria-labelledby="registration-collections-title"
    >
      <div className="finance-section-heading">
        <div>
          <span className="finance-section-kicker">
            {t("detail.finance.registrationOnlyKicker")}
          </span>
          <h2 id="registration-collections-title">{t("collections.title")}</h2>
        </div>
        {data && data.days.length > 0 ? (
          <label className="collection-range">
            {t("collections.range")}
            <select
              value={range}
              onChange={(e) => setRange(e.target.value as typeof range)}
            >
              <option value="all">{t("collections.all")}</option>
              <option value="30">{t("collections.last30")}</option>
              <option value="90">{t("collections.last90")}</option>
            </select>
          </label>
        ) : null}
      </div>
      <p className="finance-invoice-note">{t("collections.description")}</p>
      {stale ? (
        <p className="collection-warning" role="status">
          {t("collections.stale")}
        </p>
      ) : null}
      {!data ? (
        <p className="overview-state">{t("collections.unavailable")}</p>
      ) : (
        <>
          {data.undatedInvoiceCount > 0 ? (
            <p className="collection-warning">
              {t("collections.missing", {
                count: number(data.undatedInvoiceCount),
              })}{" "}
              <RiyalAmount minor={data.undatedAmountMinor} />
            </p>
          ) : null}
          {days.length === 0 ? (
            <p className="overview-state">
              {t(
                data.undatedInvoiceCount
                  ? "collections.noDates"
                  : "collections.empty",
              )}
            </p>
          ) : (
            <>
              <dl className="collection-summary">
                <div>
                  <dt>{t("collections.count")}</dt>
                  <dd>{number(totals.count)}</dd>
                </div>
                <div>
                  <dt>{t("collections.amount")}</dt>
                  <dd>
                    <RiyalAmount minor={totals.amount} />
                  </dd>
                </div>
                <div>
                  <dt>{t("collections.period")}</dt>
                  <dd>
                    {dayLabel(days[0]!.date)} — {dayLabel(data.throughDate)}
                  </dd>
                </div>
              </dl>
              <p className="collection-help">{t("collections.help")}</p>
              <div
                className="collection-chart-scroll"
                role="region"
                aria-label={t("collections.title")}
                tabIndex={0}
                dir="ltr"
              >
                <svg
                  className="collection-chart"
                  viewBox={`0 0 ${width} 280`}
                  style={{ minWidth: width }}
                  role="group"
                  aria-label={t("collections.title")}
                >
                  {[...new Set([0, Math.ceil(maximum / 2), maximum])].map(
                    (tick) => (
                      <g key={tick} aria-hidden="true">
                        <line
                          x1="54"
                          y1={220 - (tick / maximum) * 180}
                          x2={width - 20}
                          y2={220 - (tick / maximum) * 180}
                        />
                        <text
                          x="44"
                          y={225 - (tick / maximum) * 180}
                          textAnchor="end"
                        >
                          {number(tick)}
                        </text>
                      </g>
                    ),
                  )}
                  {days.map((day, index) => {
                    const slot = plotWidth / days.length,
                      x = 60 + index * slot,
                      height = (day.invoiceCount / maximum) * 180;
                    return (
                      <g key={day.date}>
                        <rect
                          className={`collection-bar ${selected?.date === day.date ? "collection-bar--selected" : ""}`}
                          x={x}
                          y={220 - Math.max(height, 2)}
                          width={Math.max(4, slot - 8)}
                          height={Math.max(height, 2)}
                        />
                        <rect
                          className="collection-hit"
                          x={x - 3}
                          y="30"
                          width={slot}
                          height="198"
                          tabIndex={0}
                          role="button"
                          aria-label={pointLabel(day)}
                          onFocus={() => setSelectedDate(day.date)}
                          onMouseEnter={() => setSelectedDate(day.date)}
                          onClick={() => setSelectedDate(day.date)}
                          onKeyDown={(event) => {
                            if (event.key === "Enter" || event.key === " ") {
                              event.preventDefault();
                              setSelectedDate(day.date);
                            }
                          }}
                        >
                          <title>{pointLabel(day)}</title>
                        </rect>
                        {index % labelEvery === 0 ||
                        index === days.length - 1 ? (
                          <text
                            aria-hidden="true"
                            x={x + slot / 2}
                            y="250"
                            textAnchor="middle"
                          >
                            {date(`${day.date}T12:00:00Z`, {
                              month: "short",
                              day: "numeric",
                              timeZone: "Asia/Riyadh",
                            })}
                          </text>
                        ) : null}
                      </g>
                    );
                  })}
                </svg>
              </div>
              <p className="collection-selection" aria-live="polite">
                {selected ? pointLabel(selected) : ""}
              </p>
              <details className="collection-data">
                <summary>{t("collections.table")}</summary>
                <div className="expense-table-wrap">
                  <table className="expense-table">
                    <thead>
                      <tr>
                        <th>{t("collections.date")}</th>
                        <th>{t("collections.count")}</th>
                        <th>{t("collections.amount")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {days.map((day) => (
                        <tr key={day.date}>
                          <th scope="row">{dayLabel(day.date)}</th>
                          <td>{number(day.invoiceCount)}</td>
                          <td>
                            <RiyalAmount minor={day.amountMinor} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </details>
            </>
          )}
        </>
      )}
    </section>
  );
}
